import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecipePrint() {
  const [searchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  const printDate = new Date().toLocaleDateString('en-ZA', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  useEffect(() => {
    const ids = (searchParams.get('ids') || '').split(',').filter(Boolean);
    if (ids.length === 0) { setLoading(false); return; }

    const fetchAll = async () => {
      try {
        const results = await Promise.all(
          ids.map(id => axios.get(`${API}/recipes/${id}`, { withCredentials: true }).catch(() => null))
        );
        setRecipes(results.filter(r => r).map(r => r.data));
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [searchParams]);

  useEffect(() => {
    if (!loading && recipes.length > 0) {
      setTimeout(() => window.print(), 600);
    }
  }, [loading, recipes]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 48 }}>Loading recipes...</div>;
  }

  if (recipes.length === 0) {
    return <div style={{ textAlign: 'center', padding: 48 }}>No recipes to print.</div>;
  }

  return (
    <>
      <style>{`
        @media print {
          /* Hide EVERYTHING from the app shell */
          body, html { margin: 0 !important; padding: 0 !important; background: white !important; }
          
          /* Nuke the sidebar, header, overlays — all layout chrome */
          nav, aside, header,
          .no-print, .screen-only,
          [data-testid="sidebar"],
          [data-testid="mobile-menu-btn"] {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
            position: absolute !important;
            left: -9999px !important;
            border: none !important;
            box-shadow: none !important;
          }

          /* Reset all layout wrappers so content fills the page */
          #root > *, .min-h-screen > * {
            border: none !important;
            box-shadow: none !important;
          }
          main {
            margin-left: 0 !important;
            padding-left: 0 !important;
          }
          .flex, .min-h-screen {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* The main content area has lg:ml-64 for sidebar — remove it */
          .lg\\:ml-64 {
            margin-left: 0 !important;
          }
          /* Mobile header spacer */
          .lg\\:pt-0 {
            padding-top: 0 !important;
          }

          @page { margin: 28mm 14mm 22mm 14mm; }
        }

        .rp-page {
          font-family: 'Manrope', 'Segoe UI', Arial, sans-serif;
          color: #000;
          max-width: 720px;
          margin: 0 auto;
          padding: 16px 24px;
        }

        /* --- Main heading --- */
        .rp-main-heading {
          text-align: center;
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.3px;
          color: #000;
          margin: 0 0 4px 0;
        }
        .rp-main-sub {
          text-align: center;
          font-size: 10px;
          color: #888;
          letter-spacing: 0.5px;
          margin: 0 0 16px 0;
          padding-bottom: 10px;
          border-bottom: 2px solid #333;
        }

        /* --- Recipe card --- */
        .rp-card {
          padding: 10px 0 14px 0;
          border-bottom: 1px solid #ccc;
          margin-bottom: 6px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .rp-card:last-child { border-bottom: none; }

        /* Keep the recipe header + first row together */
        .rp-card-header {
          break-inside: avoid;
        }

        .rp-name {
          font-size: 15px;
          font-weight: 800;
          color: #000;
          margin: 0;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .rp-meta {
          font-size: 9px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #666;
          margin: 1px 0 6px 0;
        }

        /* --- Ingredients table --- */
        .rp-section-title {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: #333;
          border-bottom: 1px solid #aaa;
          padding-bottom: 2px;
          margin: 0 0 4px 0;
        }

        .rp-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .rp-table th {
          text-align: left; font-size: 9px; font-weight: 600;
          text-transform: uppercase; color: #888; padding: 3px 4px;
          border-bottom: 1px solid #bbb; letter-spacing: 0.3px;
        }
        .rp-table td {
          padding: 3px 4px;
          border-bottom: 1px solid #eee;
          break-inside: avoid;
        }
        .rp-table tr:last-child td { border-bottom: none; }
        .rp-table .qty-cell {
          white-space: nowrap;
          font-weight: 600;
        }

        /* --- Two column layout --- */
        .rp-two-col { display: flex; gap: 20px; }
        .rp-col { flex: 1; min-width: 0; }

        /* --- Recipe image --- */
        .rp-recipe-img {
          width: 64px; height: 64px;
          object-fit: cover; border-radius: 6px;
          flex-shrink: 0;
          border: 1px solid #ddd;
        }

        /* --- Method --- */
        .rp-method { font-size: 11px; line-height: 1.6; margin: 0; padding: 0 0 0 16px; }
        .rp-method li { margin-bottom: 6px; }
        .rp-method li::marker { font-weight: 700; }

        /* --- Step media --- */
        .rp-step-media {
          display: block;
          max-width: 140px; max-height: 100px;
          object-fit: cover; border-radius: 4px;
          margin-top: 3px;
          border: 1px solid #ddd;
        }

        /* --- Footer --- */
        .rp-footer {
          margin-top: 16px;
          padding-top: 6px;
          border-top: 1px solid #ccc;
          font-size: 9px;
          color: #999;
          display: flex;
          justify-content: space-between;
        }
      `}</style>

      <div className="rp-page" data-testid="recipe-print-page">
        {/* Main heading */}
        <div className="rp-card-header">
          <h1 className="rp-main-heading">The Merchant Coffee Recipes</h1>
          <p className="rp-main-sub">{recipes.length} recipe{recipes.length !== 1 ? 's' : ''} &middot; {printDate}</p>
        </div>

        {recipes.map((recipe) => {
          const methodSteps = (recipe.method || []).map(s =>
            typeof s === 'string' ? { text: s, media_url: null, media_type: null } : s
          ).filter(s => s && (s.text || s.media_url));

          return (
            <div key={recipe.recipe_id} className="rp-card">
              <div className="rp-card-header" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                {recipe.image_url && (
                  <img
                    src={recipe.image_url}
                    alt=""
                    className="rp-recipe-img"
                  />
                )}
                <div>
                  <div className="rp-name">{recipe.name}</div>
                  <div className="rp-meta">
                    {recipe.category || ''}
                    {recipe.portions > 1 ? ` · ${recipe.portions} portions` : ''}
                    {recipe.description ? ` · ${recipe.description}` : ''}
                  </div>
                </div>
              </div>

              <div className="rp-two-col">
                <div className="rp-col">
                  <div className="rp-section-title">Ingredients</div>
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipe.ingredients?.map((ing, idx) => (
                        <tr key={idx}>
                          <td>{ing.name || ing.stock_item_name || ing.item_name}</td>
                          <td className="qty-cell">{ing.quantity} {ing.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {methodSteps.length > 0 && (
                  <div className="rp-col">
                    <div className="rp-section-title">Method</div>
                    <ol className="rp-method">
                      {methodSteps.map((step, idx) => (
                        <li key={idx}>
                          {step.text}
                          {step.media_url && step.media_type === 'image' && (
                            <img src={step.media_url} alt={`Step ${idx + 1}`} className="rp-step-media" />
                          )}
                          {step.media_url && step.media_type === 'video' && (
                            <img src={step.media_url} alt={`Step ${idx + 1} video`} className="rp-step-media" style={{ opacity: 0.85 }} />
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="rp-footer">
          <span>Printed: {printDate}</span>
          <span>The Merchant Coffee</span>
          <span>{recipes.length} recipe{recipes.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </>
  );
}
